import React, { useEffect, useState, useRef } from 'react';
import Loader from './Loader';
import { apiRequest } from '../api';
import ENV_CONFIG from '../config';
import { Container, Card, Table, Spinner, Alert, Form, Row, Col, Modal, Button } from 'react-bootstrap';

function ListOrganization() {
  const [organizations, setOrganizations] = useState([]);
  const [error, setError] = useState(null);
  const [expandedOrg, setExpandedOrg] = useState(null);
  const [organizationData, setOrganizationData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedConfig, setExpandedConfig] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const tableRef = useRef(null);

  // Get environment from localStorage, default to 'dev'
  const environment = localStorage.getItem('environment') || 'dev';
  const { baseUrl } = ENV_CONFIG[environment];

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const data = await apiRequest(`${baseUrl}/api/org/organizations`);
        
        if (data && Array.isArray(data.organization_data)) {
          const orgs = data.organization_data.map(org => ({
            name: org.organization_name,
            account: org.account_name,
            group: org.group_name
          }));
          setOrganizations(orgs);
          setOrganizationData(data.organization_data);
        } else {
          throw new Error('Data does not contain an array of organization data');
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, [baseUrl]);

  const toggleDetails = (org) => {
    setExpandedOrg(expandedOrg === org ? null : org);
  };

  const toggleConfigDetails = (index) => {
    setExpandedConfig(expandedConfig === index ? null : index);
  };

  const handleShowConfigModal = (org) => {
    setSelectedConfig(org);
    setShowConfigModal(true);
  };

  const handleCloseConfigModal = () => {
    setShowConfigModal(false);
    setSelectedConfig(null);
  };

  const distinctOrganizations = [...new Set(organizations.map(org => org.name))];

  // Sort organizationData by organization_name, account_name, group_name
  const sortedData = [...organizationData].sort((a, b) => {
    if (a.organization_name !== b.organization_name) {
      return a.organization_name.localeCompare(b.organization_name);
    }
    if (a.account_name !== b.account_name) {
      return a.account_name.localeCompare(b.account_name);
    }
    return a.group_name.localeCompare(b.group_name);
  });

  // Calculate row spans for organization, account, and group
  const rowSpanMap = {};
  sortedData.forEach((org, idx) => {
    const orgKey = org.organization_name;
    const accKey = org.organization_name + '|' + org.account_name;
    const groupKey = org.organization_name + '|' + org.account_name + '|' + org.group_name;

    rowSpanMap.org = rowSpanMap.org || {};
    rowSpanMap.acc = rowSpanMap.acc || {};
    rowSpanMap.group = rowSpanMap.group || {};

    rowSpanMap.org[orgKey] = (rowSpanMap.org[orgKey] || 0) + 1;
    rowSpanMap.acc[accKey] = (rowSpanMap.acc[accKey] || 0) + 1;
    rowSpanMap.group[groupKey] = (rowSpanMap.group[groupKey] || 0) + 1;
  });

  // Track last seen for row spanning
  let lastOrg = '', lastAcc = '', lastGroup = '';
  let orgIndex = {}, accIndex = {}, groupIndex = {};

  // Resizing logic
  const startResize = (index, e) => {
    const startX = e.clientX;
    const th = tableRef.current.querySelectorAll('th')[index];
    const startWidth = th.offsetWidth;

    const onMouseMove = (moveEvent) => {
      const newWidth = startWidth + (moveEvent.clientX - startX);
      th.style.width = `${newWidth}px`;
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  return (
    <Container className="py-4">
      <Card className="shadow">
        <Card.Header>
          <h4 className="mb-0">Organizations</h4>
        </Card.Header>
        <Card.Body>
          <style>
            {`
              .rotating-loader {
                animation: rotate 2s linear infinite;
              }

              @keyframes rotate {
                from {
                  transform: rotate(0deg);
                }
                to {
                  transform: rotate(360deg);
                }
              }
            `}
          </style>
          <p>Total Organizations: {[...new Set(organizationData.map(org => org.organization_name))].length}</p>
          {loading ? (
            <div className="d-flex justify-content-center my-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : error ? (
            <Alert variant="danger">{error}</Alert>
          ) : (
            <Table striped bordered hover responsive ref={tableRef}>
              <thead className="table-light">
                <tr>
                  <th>Organization</th>
                  <th>Account</th>
                  <th>Group</th>
                  <th>Group Code</th>
                  <th>Group Code Config</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((org, idx) => {
                  const orgKey = org.organization_name;
                  const accKey = org.organization_name + '|' + org.account_name;
                  const groupKey = org.organization_name + '|' + org.account_name + '|' + org.group_name;

                  // Only show cell if first occurrence
                  const showOrg = orgIndex[orgKey] === undefined;
                  const showAcc = accIndex[accKey] === undefined;
                  const showGroup = groupIndex[groupKey] === undefined;

                  if (showOrg) orgIndex[orgKey] = idx;
                  if (showAcc) accIndex[accKey] = idx;
                  if (showGroup) groupIndex[groupKey] = idx;

                  return (
                    <tr key={idx}>
                      {showOrg && (
                        <td rowSpan={rowSpanMap.org[orgKey]}>
                          <span style={{ color: 'blue' }}>{org.organization_name}</span>
                        </td>
                      )}
                      {showAcc && (
                        <td rowSpan={rowSpanMap.acc[accKey]}>
                          {org.account_name}
                        </td>
                      )}
                      {showGroup && (
                        <td rowSpan={rowSpanMap.group[groupKey]}>
                          {org.group_name}
                        </td>
                      )}
                      <td>
                        <a href="#" onClick={e => { e.preventDefault(); handleShowConfigModal(org); }} style={{ color: 'blue' }}>
                          {org.code}
                        </a>
                      </td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleShowConfigModal(org)}
                        >
                          View Config
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}

          {/* Modal for Group Code Config */}
          <Modal show={showConfigModal} onHide={handleCloseConfigModal} size="lg" centered>
            <Modal.Header closeButton>
              <Modal.Title>Details for Group Code: {selectedConfig?.code}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {selectedConfig ? (
                <Form>
                  <Form.Group as={Row} className="mb-3" controlId="formCode">
                    <Form.Label column sm={4}>Code</Form.Label>
                    <Col sm={8}>
                      <Form.Control
                        type="text"
                        value={selectedConfig.code || ''}
                        disabled
                      />
                    </Col>
                  </Form.Group>
                  <Form.Group as={Row} className="mb-3" controlId="formCapacity">
                    <Form.Label column sm={4}>Capacity</Form.Label>
                    <Col sm={8}>
                      <Form.Control
                        type="number"
                        value={selectedConfig.capacity || ''}
                        disabled
                        min="0"
                      />
                    </Col>
                  </Form.Group>
                  <Form.Group as={Row} className="mb-3" controlId="formAcceptedCount">
                    <Form.Label column sm={4}>Accepted Count</Form.Label>
                    <Col sm={8}>
                      <Form.Control
                        type="number"
                        value={selectedConfig.accepted_count || ''}
                        disabled
                        min="0"
                      />
                    </Col>
                  </Form.Group>
                  <Form.Group as={Row} className="mb-3" controlId="formExpiration">
                    <Form.Label column sm={4}>Expiration</Form.Label>
                    <Col sm={8}>
                      <Form.Control
                        type="text"
                        value={selectedConfig.expiration 
                          ? new Date(selectedConfig.expiration).toLocaleDateString()
                          : 'N/A'}
                        disabled
                      />
                    </Col>
                  </Form.Group>
                  <Card className="mb-3">
                    <Card.Header>Policy</Card.Header>
                    <Card.Body>
                      <Row className="mb-2">
                        <Col md={4}>
                          <div>
                            <strong>CF: </strong>
                            {selectedConfig.policy?.isCFEnable ? '✔️' : '❌'}
                          </div>
                        </Col>
                        <Col md={4}>
                          <div>
                            <strong>File Upload: </strong>
                            {selectedConfig.policy?.isFileUpload ? '✔️' : '❌'}
                          </div>
                        </Col>
                        <Col md={4}>
                          <div>
                            <strong>Show Feature Description: </strong>
                            {selectedConfig.policy?.showFeatureDescription ? '✔️' : '❌'}
                          </div>
                        </Col>
                      </Row>
                      <Row className="mb-2">
                        <Col md={6}>
                          <Form.Label>Notification Medium</Form.Label>
                          <div>
                            <strong>SMS: </strong>
                            {selectedConfig.policy?.notification?.medium?.includes('SMS') ? '✔️' : '❌'}
                            <span style={{ margin: '0 10px' }}></span>
                            <strong>PN: </strong>
                            {selectedConfig.policy?.notification?.medium?.includes('PN') ? '✔️' : '❌'}
                          </div>
                        </Col>
                        <Col md={3}>
                          <Form.Label>Schedule SMS/PN Notification</Form.Label>
                          <Form.Select
                            name="schedule"
                            value={selectedConfig.policy?.notification?.schedule || ''}
                            disabled
                          >
                            <option value="">NA</option>
                            <option value="MORNING">MORNING</option>
                            <option value="MIDDAY">MIDDAY</option>
                            <option value="EVENING">EVENING</option>
                            <option value="DAILY">DAILY</option>
                            <option value="WEEKLY">WEEKLY</option>
                          </Form.Select>
                        </Col>
                        <Col md={3}>
                          <Form.Label>Trigger Type</Form.Label>
                          <Form.Select
                            name="triggerType"
                            value={selectedConfig.policy?.notification?.triggerType || ''}
                            disabled
                          >
                            <option value="">NA</option>
                            <option value="AUTOMATIC">AUTOMATIC</option>
                            <option value="MANUAL">MANUAL</option>
                          </Form.Select>
                        </Col>
                      </Row>
                      <Row className="mb-2">
                        <Col md={6}>
                          <Form.Label>Default Signup</Form.Label>
                          <Form.Select
                            name="userDefaultSignup"
                            value={selectedConfig.policy?.userDefaultSignup || ''}
                            disabled
                          >
                            <option value="">NA</option>
                            <option value="PHONE_NUMBER">PHONE_NUMBER</option>
                            <option value="EMAIL">EMAIL</option>
                          </Form.Select>
                        </Col>
                        <Col md={6}>
                          <Form.Label>Country Code</Form.Label>
                          <Form.Control
                            type="text"
                            value={selectedConfig.policy?.defaultCountryCode || ''}
                            disabled
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col md={6}>
                          <Form.Label>Voice Analysis Mode</Form.Label>
                          <Form.Select
                            name="voiceAnalysisMode"
                            value={selectedConfig.policy?.voiceAnalysisMode || ''}
                            disabled
                          >
                            <option value="NON_CONTINUOUS_PASSIVE">NON_CONTINUOUS_PASSIVE</option>
                            <option value="CUED">CUED</option>
                            <option value="PASSIVE">PASSIVE</option>
                          </Form.Select>
                        </Col>
                        <Col md={6}>
                          <Form.Label>Verifiable Segment Size</Form.Label>
                          <Form.Select
                            name="verifiableSegmentSize"
                            value={selectedConfig.policy?.verifiableSegmentSize || ''}
                            disabled
                          >
                            <option value="3">3</option>
                            <option value="6">6</option>
                            <option value="9">9</option>
                          </Form.Select>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Form>
              ) : (
                <Alert variant="warning">No details found.</Alert>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseConfigModal}>
                Close
              </Button>
            </Modal.Footer>
          </Modal>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default ListOrganization;